import { PLAID_CONFIG } from '../config/plaid';
import { store } from '../store';
import { setUploadProgress, setCategorizationStatus, setError, setTransactions } from '../store/transactionsSlice';

// These interfaces are used by the Plaid API responses
export interface PlaidTransaction {
  transaction_id: string;
  date: string;
  amount: number;
  merchant_name: string;
  category: string[];
  name: string;
  account_id?: string;
}

export interface PlaidAccount {
  account_id: string;
  name: string;
  balances: {
    available: number;
    current: number;
    limit?: number;
  };
}

interface PlaidLinkConfig {
  env: string;
  clientId: string;
  products: readonly string[];
  language: string;
  countryCodes: readonly string[];
  token: string;
  onSuccess: (publicToken: string, metadata: any) => void;
  onExit: (error: Error | null) => void;
  onEvent: (eventName: string) => void;
}

export class PlaidService {
  private static BASE_URL = `${import.meta.env.VITE_API_URL}/api`;

  static async createLinkToken(userId: string): Promise<string> {
    try {
      console.log('Making request to create link token:', {
        url: `${this.BASE_URL}/create_link_token`,
        userId
      });

      const response = await fetch(`${this.BASE_URL}/create_link_token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });

      console.log('Create link token response status:', response.status);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create link token');
      }

      if (!data.link_token) {
        throw new Error('No link token received from server');
      }

      console.log('Create link token success:', {
        expiration: data.expiration,
        request_id: data.request_id
      });

      return data.link_token;
    } catch (error) {
      console.error('Error creating link token:', error);
      throw error;
    }
  }

  static async exchangePublicToken(publicToken: string, userId: string): Promise<void> {
    try {
      console.log('Exchanging public token for access token');

      // Update progress to show we're starting
      store.dispatch(setUploadProgress(25));

      const response = await fetch(`${this.BASE_URL}/exchange_public_token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          public_token: publicToken,
          userId 
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to exchange public token');
      }

      console.log('Successfully exchanged public token:', {
        success: data.success,
        hasTransactions: data.hasTransactions
      });

      // Update progress after successful token exchange
      store.dispatch(setUploadProgress(75));

      // If we got transactions directly from the exchange endpoint
      if (data.transactions) {
        // Process and store the transactions
        store.dispatch(setUploadProgress(100));
        store.dispatch(setCategorizationStatus('succeeded'));
        store.dispatch(setTransactions(data.transactions));
      } else {
        throw new Error('No transactions received from server');
      }
    } catch (error) {
      console.error('Error exchanging public token:', error);
      store.dispatch(setCategorizationStatus('failed'));
      store.dispatch(setError(error.message));
      throw error;
    }
  }

  static async openLink(linkToken: string, onSuccess: (publicToken: string, metadata: any) => void): Promise<void> {
    const config: PlaidLinkConfig = {
      token: linkToken,
      onSuccess,
      onExit: (err) => {
        if (err != null) {
          console.error('Plaid Link exit with error:', err);
        }
      },
      onEvent: (eventName) => {
        console.log('Plaid Link event:', eventName);
      },
      env: PLAID_CONFIG.env,
      clientId: PLAID_CONFIG.clientId,
      products: PLAID_CONFIG.products,
      language: PLAID_CONFIG.language,
      countryCodes: PLAID_CONFIG.countryCodes,
    };

    // Load the Plaid Link script dynamically
    const script = document.createElement('script');
    script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
    script.async = true;
    script.onload = () => {
      // @ts-ignore - Plaid is loaded globally
      const handler = window.Plaid.create({
        ...config,
        env: 'sandbox',
        token: linkToken,
      });
      handler.open();
    };
    document.head.appendChild(script);
  }
}
